import { useEffect, useRef, useState } from "react";

/**
 * Parses a label like "15+" / "500+" / "100%" / "24/7" / "12"
 * and returns: { target: number|null, prefix: "", suffix: "+" }
 * If the value cannot be animated (e.g. "24/7"), target is null.
 */
function parseTarget(raw) {
  const m = String(raw).match(/^(\D*)(\d+(?:[.,]\d+)?)(.*)$/);
  if (!m) return { target: null, prefix: raw, suffix: "" };
  const prefix = m[1];
  const number = Number(m[2].replace(",", "."));
  const suffix = m[3];
  // Skip animations on shapes like "24/7" or "1/2"
  if (suffix.includes("/")) return { target: null, prefix: raw, suffix: "" };
  return { target: number, prefix, suffix };
}

/**
 * Animated counter that runs once when the element enters the viewport.
 * Falls back to static rendering if the value isn't numeric (e.g. "24/7").
 */
const AnimatedCounter = ({ value, duration = 1800, className = "", style }) => {
  const { target, prefix, suffix } = parseTarget(value);
  const [display, setDisplay] = useState(target === null ? value : 0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (target === null) return;
    const node = ref.current;
    if (!node) return;

    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const startTime = performance.now();
      const tick = (now) => {
        const elapsed = now - startTime;
        const t = Math.min(elapsed / duration, 1);
        // ease-out cubic for a natural finish
        const eased = 1 - Math.pow(1 - t, 3);
        const isInt = Number.isInteger(target);
        const current = target * eased;
        setDisplay(isInt ? Math.round(current) : Number(current.toFixed(1)));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) start();
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [target, duration]);

  if (target === null) {
    return (
      <span ref={ref} className={className} style={style}>
        {value}
      </span>
    );
  }

  return (
    <span ref={ref} className={className} style={style}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

export default AnimatedCounter;
